/**
 * React hook for bot protection
 *
 * Usage:
 * const { securityHeaders, isReady, solveChallenge } = useBotProtection();
 *
 * // Add to fetch requests
 * fetch('/api/login', {
 *   headers: { ...securityHeaders }
 * })
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  encodeFingerprint,
  InteractionTracker,
  solveProofOfWork,
} from "../lib/botProtection";

/**
 * Bot protection hook for forms and API requests
 */
export const useBotProtection = (options = {}) => {
  const {
    enableFingerprint = true,
    enableTracking = true,
    autoRefreshInterval = 300000, // Refresh fingerprint every 5 minutes
  } = options;

  const [fingerprint, setFingerprint] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoadingChallenge, setIsLoadingChallenge] = useState(false);
  const [challengeProgress, setChallengeProgress] = useState(0);
  const trackerRef = useRef(null);
  const formLoadedAtRef = useRef(Date.now());

  // Initialize tracker
  useEffect(() => {
    if (enableTracking) {
      trackerRef.current = new InteractionTracker();
    }

    return () => {
      trackerRef.current = null;
    };
  }, [enableTracking]);

  // Collect fingerprint
  useEffect(() => {
    if (!enableFingerprint) {
      setIsReady(true);
      return;
    }

    const collectFp = async () => {
      try {
        const fp = await encodeFingerprint();
        setFingerprint(fp);
        setIsReady(true);
      } catch (error) {
        console.error("Failed to collect fingerprint:", error);
        setIsReady(true);
      }
    };
    collectFp();

    // Refresh periodically
    const interval = setInterval(collectFp, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [enableFingerprint, autoRefreshInterval]);

  /**
   * Get security headers for fetch requests
   */
  const getSecurityHeaders = useCallback(() => {
    const headers = {
      "X-Form-Loaded-At": formLoadedAtRef.current.toString(),
    };

    if (fingerprint) {
      headers["X-Device-Fingerprint"] = fingerprint;
    }

    if (trackerRef.current) {
      headers["X-User-Interactions"] = trackerRef.current.encode();
    }

    return headers;
  }, [fingerprint]);

  /**
   * Solve a Proof-of-Work challenge
   */
  const solveChallenge = useCallback(async (challenge, difficulty) => {
    setIsLoadingChallenge(true);
    setChallengeProgress(0);

    try {
      const result = await solveProofOfWork(
        challenge,
        difficulty,
        (progress) => {
          setChallengeProgress(progress);
        },
      );

      return result;
    } finally {
      setIsLoadingChallenge(false);
      setChallengeProgress(0);
    }
  }, []);

  /**
   * Reset form load time (call when navigating to a new form)
   */
  const resetFormTimer = useCallback(() => {
    formLoadedAtRef.current = Date.now();
  }, []);

  /**
   * Get interaction summary
   */
  const getInteractions = useCallback(
    () => trackerRef.current?.getSummary() || null,
    [],
  );

  return {
    // State
    isReady,
    fingerprint,
    isLoadingChallenge,
    challengeProgress,

    // Methods
    getSecurityHeaders,
    solveChallenge,
    resetFormTimer,
    getInteractions,

    // Convenience alias
    securityHeaders: getSecurityHeaders(),
  };
};

/**
 * Hook for Cloudflare Turnstile
 */
export const useTurnstile = (siteKey, options = {}) => {
  const [token, setToken] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const widgetIdRef = useRef(null);
  const containerRef = useRef(null);

  const { autoRender = true, theme = "auto" } = options;

  // Load Turnstile script
  useEffect(() => {
    if (!siteKey) return;

    // Check if already loaded
    if (window.turnstile) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      setError(new Error("Failed to load Turnstile"));
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup widget if exists
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [siteKey]);

  /**
   * Render Turnstile widget
   */
  const render = useCallback(
    (containerId) => {
      if (!isLoaded || !siteKey || !window.turnstile) return;

      // Remove existing widget
      if (widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
      }

      widgetIdRef.current = window.turnstile.render(`#${containerId}`, {
        sitekey: siteKey,
        theme,
        callback: (newToken) => {
          setToken(newToken);
          setError(null);
        },
        "error-callback": () => {
          setError(new Error("Turnstile verification failed"));
          setToken(null);
        },
        "expired-callback": () => {
          setToken(null);
        },
      });

      containerRef.current = containerId;
    },
    [isLoaded, siteKey, theme],
  );

  /**
   * Reset Turnstile widget
   */
  const reset = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setToken(null);
    }
  }, []);

  /**
   * Get token header for requests
   */
  const getHeader = useCallback(
    () => (token ? { "X-Turnstile-Token": token } : {}),
    [token],
  );

  // Auto-render if container ref is set
  useEffect(() => {
    if (autoRender && isLoaded && containerRef.current) {
      render(containerRef.current);
    }
  }, [autoRender, isLoaded, render]);

  return {
    token,
    isLoaded,
    error,
    render,
    reset,
    getHeader,
    containerRef,
  };
};

export default useBotProtection;
